import React, { FC, MouseEventHandler, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexRow } from '@/atoms/FlexContainers'
import { FireIcon } from '@/atoms/Icons/FireIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { CaptionXS, LabelSM } from '@/atoms/Text'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useEnhancedShowtimeContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import { convertUTCToLocalTime } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/utils'

interface ShowTimeLinkProps {
  showTime: TheatricalShowtime
  countryCode: string
  onClick: MouseEventHandler<HTMLButtonElement>
  isDisabled?: boolean
}

export const ShowTimeLink: React.FC<ShowTimeLinkProps> = ({ showTime, countryCode, onClick, isDisabled = false }) => {
  const { query } = useSafeRouter()
  const { isShowtimeEnhanced } = useEnhancedShowtimeContext()
  const { locale } = useLocale()
  const [isLoading, setIsLoading] = useState(false)

  const hasPurchaseTicketsUrl = !!showTime?.ticketUrl
  const isSoldOut = showTime?.availableInventory === 0 && (showTime?.totalSeats ?? 0) > 0
  const hasShowTimePassed = showTime?.utcStartTime && new Date(showTime?.utcStartTime) < new Date()
  const shouldDisplayFlame = useMemo(
    () => isShowtimeEnhanced(showTime?.ticketingId ?? ''),
    [isShowtimeEnhanced, showTime?.ticketingId],
  )
  const startTime = convertUTCToLocalTime(showTime?.utcStartTime, showTime?.localTimeOffset, locale)

  const isLinkEnabled = useMemo(() => {
    if (query?.promo && countryCode === 'PH' && !hasShowTimePassed) return true
    return !isSoldOut && !hasShowTimePassed && hasPurchaseTicketsUrl && !isDisabled
  }, [hasPurchaseTicketsUrl, hasShowTimePassed, isDisabled, isSoldOut, query?.promo, countryCode])

  useEffect(() => {
    if (isLoading && isDisabled) setIsLoading(true)
    if (isLoading && !isDisabled) setIsLoading(false)
  }, [isDisabled, isLoading])

  const iconVariant: IconType | null = useMemo(() => {
    if (isSoldOut) return 'sold-out'
    if (shouldDisplayFlame) return 'flame'
    return null
  }, [isSoldOut, shouldDisplayFlame])

  if (!startTime) return null

  return (
    <Button
      onClick={(e) => {
        onClick(e)
        setIsLoading(true)
      }}
      disabled={!isLinkEnabled}
      className={classNames(
        'relative h-10 w-full max-w-[5.4rem] flex gap-1 items-center shrink-0 p-0 md:max-w-[6.5rem]',
        !isLinkEnabled && 'pointer-events-none',
      )}
      variant="oxide-300"
      data-dd-action-name="Tickets Showtime Selected"
    >
      {isLoading && <LoadingSpinnerIcon color="core-gray-600" size={16} />}
      <FlexRow className="gap-1">
        <LabelSM weight="semibold">{startTime}</LabelSM>
      </FlexRow>
      {iconVariant && <ShowtimeIcon variant={iconVariant} />}
    </Button>
  )
}

type IconType = 'sold-out' | 'flame'

interface showtimeIconProps {
  variant: IconType | null
}
const ShowtimeIcon: FC<showtimeIconProps> = ({ variant }) => {
  const { t } = useTranslate('tickets')

  if (variant === 'flame') {
    return (
      <div className="absolute -right-2 -top-3 rounded-full border-[1.5px] border-warm-01 bg-warm-01 p-1">
        <FireIcon color="white" size={12} />
      </div>
    )
  }

  if (variant === 'sold-out') {
    return (
      <div className="absolute -right-2 -top-3 shrink-0 rounded-full border-[1.5px] border-warm-01 bg-warm-01 px-1.5">
        <CaptionXS weight="bold" className="text-[0.5rem]" color="white">
          {t('soldOut', 'Sold Out')}
        </CaptionXS>
      </div>
    )
  }

  return null
}
